import type { ChatTurn } from 'ProdantixSdk/chat/client';
import { decode, encodeConnect, pong, type SocketAuth } from 'ProdantixSdk/chat/socket/frames';
import { TransportError } from 'ProdantixSdk/core/errors';
import type { SocketFactory, SocketLike } from 'ProdantixSdk/core/types';

export type { SocketFactory, SocketLike } from 'ProdantixSdk/core/types';

export interface ConversationSocketOptions {
  auth: SocketAuth;
  /// The `prodantix-ws` origin, told to us by the config endpoint rather than
  /// derived from the edge host. The edge and ws are different services on
  /// different hostnames, and computing one from the other is a guess dressed as
  /// a rule.
  host: string;
  onReady: () => void;
  onTurn: (turn: ChatTurn) => void;
  /// Called whenever the connection is not carrying turns, so the caller can
  /// bring polling back. `retryable` is false when the server refused the
  /// handshake, which no amount of retrying improves.
  onClosed: (retryable: boolean) => void;
  socketFactory?: SocketFactory;
  /// Injected so a test does not wait real seconds.
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/// Direct websocket, never the polling transport. Two things follow from that
/// and both are load-bearing: it removes the upgrade dance and the packet
/// buffering that make a general client large, and a websocket handshake is not
/// subject to a CORS preflight, so the widget connects from any customer origin
/// without `prodantix-ws` growing a cross-origin policy it does not have.
const PATH = '/socket.io/?EIO=4&transport=websocket';

/// One conversation's live connection. It only ever listens: the widget sends
/// messages over REST, so nothing here emits a Socket.IO event.
export class ConversationSocket {
  private socket: SocketLike | null = null;
  private heartbeat: unknown = null;
  private ready = false;
  private closed = false;
  /// How long to wait for the server to say anything before assuming the
  /// connection is dead. Replaced by the server's own figures on `open`.
  private heartbeatWindow = 45_000;
  private readonly factory: SocketFactory;
  private readonly setTimer: (run: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(private readonly options: ConversationSocketOptions) {
    this.factory = options.socketFactory ?? defaultFactory;
    this.setTimer = options.setTimer ?? ((run, ms) => setTimeout(run, ms));
    this.clearTimer = options.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
  }

  open(): void {
    if (this.socket || this.closed) return;
    const url = `${this.options.host.replace(/^http/, 'ws').replace(/\/+$/, '')}${PATH}`;
    let socket: SocketLike;
    try {
      socket = this.factory(url);
    } catch {
      // A blocked or malformed origin must not throw into the customer's page.
      this.options.onClosed(false);
      return;
    }
    this.socket = socket;
    socket.onmessage = event => this.receive(String(event.data));
    socket.onerror = () => socket.close();
    socket.onclose = () => this.fell();
  }

  /// Deliberate shutdown. Never reports retryable, because the caller asked.
  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    const socket = this.socket;
    this.socket = null;
    socket?.close();
  }

  private receive(raw: string): void {
    const frame = decode(raw);
    if (frame.kind === 'open') {
      /* The server's own intervals, not ours. It sends a ping every
         `pingInterval` and closes if a pong does not follow within
         `pingTimeout`; a watchdog slightly beyond their sum notices a server
         that has stopped speaking, which a websocket does not otherwise
         surface until TCP gives up. */
      this.armHeartbeat(frame.pingInterval + frame.pingTimeout);
      this.socket?.send(encodeConnect(this.options.auth));
      return;
    }
    if (frame.kind === 'ping') {
      this.armHeartbeat();
      this.socket?.send(pong());
      return;
    }
    if (frame.kind === 'connectError' || frame.kind === 'disconnect') {
      this.stopHeartbeat();
      this.socket?.close();
      return;
    }
    if (frame.kind !== 'event') return;
    if (frame.name === 'ready') {
      this.ready = true;
      this.options.onReady();
      return;
    }
    if (frame.name === 'conversation.turn') {
      const turn = (frame.payload as { turn?: ChatTurn } | undefined)?.turn;
      if (turn) this.options.onTurn(turn);
    }
  }

  /// A close before `ready` means the handshake was refused. `realtime.rs`
  /// disconnects without an error frame on every failed handshake, deliberately,
  /// so that a refusal is not an oracle for which conversation ids exist. There
  /// is nothing to learn from retrying it.
  private fell(): void {
    this.stopHeartbeat();
    const retryable = this.ready;
    this.socket = null;
    this.ready = false;
    if (!this.closed) this.options.onClosed(retryable);
  }

  private armHeartbeat(ms?: number): void {
    if (ms !== undefined) this.heartbeatWindow = ms;
    this.stopHeartbeat();
    this.heartbeat = this.setTimer(() => this.socket?.close(), this.heartbeatWindow);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) this.clearTimer(this.heartbeat);
    this.heartbeat = null;
  }
}

function defaultFactory(url: string): SocketLike {
  const ctor = (globalThis as { WebSocket?: new (url: string) => SocketLike }).WebSocket;
  if (!ctor) throw new TransportError('no WebSocket');
  return new ctor(url);
}
